// src/Auth/AuthProvider.js
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import config from "../config";
import Cookies from "js-cookie";
import axios from "axios";
import { AuthContext } from "./AuthContext";
import { io } from "socket.io-client";

axios.defaults.baseURL = config.BASE_URL;

const AuthProvider = ({ children }) => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return Cookies.get("isAuthenticated") === "true";
  });

  const [username, setUsername] = useState(() => {
    return Cookies.get("username") || "";
  });

  const [userProfile, setUserProfile] = useState(() => {
    return Cookies.get("profile") || "";
  });

  const [isLoading, setIsLoading] = useState(true);

  // Module and navigation state
  const [activeModule, setActiveModule] = useState(() => {
    return sessionStorage.getItem("actMod") || "upload";
  });

  const [moduleChange, setModuleChange] = useState("");
  const [searchValue, setSearchValue] = useState("");

  // Trip and billing state
  const [TripIDState, setTripIDState] = useState("");
  const [reviseBillState, setReviseBillState] = useState("");

  // Chat state
  const [selectedChat, setSelectedChat] = useState(null);
  const [receivedMsg, setReceivedMsg] = useState(null);

  // NEW: Unread count state
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  // Filter states
  const [filterReports, setFilterReports] = useState(() => ({
    startDate: "",
    endDate: "",
    vendor: "",
    truckNumber: "",
  }));

  const [filterBills, setFilterBills] = useState(() => ({
    startDate: "",
    endDate: "",
    vendor: "",
    truckNumber: "",
  }));

  const [business, setBusiness] = useState("");

  // Ref to prevent multiple auth checks
  const authCheckInProgressRef = useRef(false);
  const unreadSocketRef = useRef(null);

  // Memoized setAuth function
  const setAuth = useCallback(
    (value, usernameParam, profile, businessParam) => {
      console.log("setAuth called with:", {
        value,
        usernameParam,
        profile,
        businessParam,
      });

      const newUsername = usernameParam || "";
      const newProfile = profile || "";
      const newBusiness = businessParam || "";

      setIsAuthenticated(value);
      setUsername(newUsername);
      setUserProfile(newProfile);
      setBusiness(newBusiness);

      if (value) {
        Cookies.set("isAuthenticated", "true", {
          secure: false,
          sameSite: "Strict",
          expires: 1,
        });
        if (newProfile) {
          Cookies.set("profile", newProfile, {
            secure: false,
            sameSite: "Strict",
            expires: 1,
          });
        }
        if (newUsername) {
          Cookies.set("username", newUsername, {
            secure: false,
            sameSite: "Strict",
            expires: 1,
          });
        }
      } else {
        Cookies.remove("isAuthenticated");
        Cookies.remove("profile");
        Cookies.remove("username");
        Cookies.remove("business");
        sessionStorage.removeItem("actMod");
        setActiveModule("upload");
        setSelectedChat(null);
        setReceivedMsg(null);
        setTotalUnreadCount(0); // Reset unread count on logout
      }
    },
    [],
  );

  // Filter setters
  const setFilterReportsOptimized = useCallback((newFilters) => {
    setFilterReports((prevFilters) => {
      const hasChanged = Object.keys(newFilters).some(
        (key) => prevFilters[key] !== newFilters[key],
      );
      return hasChanged ? { ...prevFilters, ...newFilters } : prevFilters;
    });
  }, []);

  const setFilterBillsOptimized = useCallback((newFilters) => {
    setFilterBills((prevFilters) => {
      const hasChanged = Object.keys(newFilters).some(
        (key) => prevFilters[key] !== newFilters[key],
      );
      return hasChanged ? { ...prevFilters, ...newFilters } : prevFilters;
    });
  }, []);

  // Active module setter
  const setActiveModuleOptimized = useCallback(
    (module) => {
      if (activeModule !== module) {
        setActiveModule(module);
        sessionStorage.setItem("actMod", module);
      }
    },
    [activeModule],
  );

  // NEW: Fetch unread count function
  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthenticated || !username) return;

    try {
      const response = await axios.get("/api/chats", {
        withCredentials: true,
      });

      const chats = response.data;
      const total = chats.reduce(
        (sum, chat) => sum + (chat.unreadCount || 0),
        0,
      );
      setTotalUnreadCount(total);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  }, [isAuthenticated, username]);

  // NEW: Decrement unread count
  const decrementUnreadCount = useCallback((count = 1) => {
    setTotalUnreadCount((prev) => Math.max(0, prev - count));
  }, []);

  // NEW: Reset unread count
  const resetUnreadCount = useCallback(() => {
    setTotalUnreadCount(0);
  }, []);

  // NEW: Fetch initial unread count
  useEffect(() => {
    if (isAuthenticated && username && !isLoading) {
      refreshUnreadCount();
    }
  }, [isAuthenticated, username, isLoading, refreshUnreadCount]);

  // NEW: Socket connection for unread count updates
  useEffect(() => {
    if (!username || !isAuthenticated || isLoading) return;

    console.log("Setting up unread count socket...");

    const newSocket = io(config.BASE_URL || "https://saleseco.mindfuturetechsupport.com", {
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      transports: ["websocket", "polling"],
    });

    newSocket.on("connect", () => {
      console.log("Unread count socket connected");
      newSocket.emit("register", { userId: username });
    });

    // Listen for new messages
    newSocket.on("receiveMessage", (message) => {
      // Only increment if message is not from current user
      if (message.senderId !== username) {
        setTotalUnreadCount((prev) => prev + 1);
      }
    });

    // Listen for messages marked as read
    newSocket.on("messagesRead", (data) => {
      if (data.readBy === username) {
        refreshUnreadCount(); // Refresh to get accurate count
      }
    });

    // Listen for chat created
    newSocket.on("chatCreated", () => {
      refreshUnreadCount();
    });

    // Listen for chat deleted
    newSocket.on("chatDeleted", () => {
      refreshUnreadCount();
    });

    unreadSocketRef.current = newSocket;

    return () => {
      console.log("Cleaning up unread count socket");
      if (unreadSocketRef.current) {
        unreadSocketRef.current.disconnect();
        unreadSocketRef.current = null;
      }
    };
  }, [username, isAuthenticated, isLoading, refreshUnreadCount]);

  // Existing auth check effect
  useEffect(() => {
    console.log("AuthProvider useEffect running");
    let isMounted = true;

    const checkAuthStatus = async () => {
      console.log("checkAuthStatus called");

      if (authCheckInProgressRef.current) {
        console.log("Auth check already in progress, skipping");
        return;
      }

      authCheckInProgressRef.current = true;

      try {
        // First set from cookies immediately for better UX
        const localAuth = Cookies.get("isAuthenticated") === "true";
        const localUsername = Cookies.get("username") || "";
        const localProfile = Cookies.get("profile") || "";
        const localBusiness = Cookies.get("business") || "";

        console.log("Local auth state:", {
          localAuth,
          localUsername,
          localProfile,
          localBusiness,
        });

        if (isMounted && localAuth) {
          console.log("Setting auth state from local cookies");
          setIsAuthenticated(true);
          setUsername(localUsername);
          setUserProfile(localProfile);
          setBusiness(localBusiness);
        }

        // Then verify with server
        console.log("Making server auth check...");
        const response = await axios.get("/check-auth", {
          withCredentials: true,
          timeout: 3000,
        });

        console.log("Server auth response:", response.data);
        console.log("Response status:", response.status);

        if (isMounted) {
          if (response.status === 200) {
            const data = response.data;

            let serverUsername, serverProfile, serverBusiness;

            if (data.authenticated === true) {
              serverUsername = data.username;
              serverProfile = data.profile;
              serverBusiness = data.business || localBusiness;
            } else if (data.username && data.profile) {
              serverUsername = data.username;
              serverProfile = data.profile;
              serverBusiness = data.business;
            } else {
              throw new Error("Invalid response format from server");
            }

            console.log("Server auth data:", {
              serverUsername,
              serverProfile,
              serverBusiness,
            });

            if (
              !localAuth ||
              serverUsername !== localUsername ||
              serverProfile !== localProfile
            ) {
              console.log("Updating auth state from server");
              setAuth(true, serverUsername, serverProfile, serverBusiness);
            } else {
              console.log("Auth state already up to date");
            }
          } else {
            console.log("Server returned non-200 status, clearing auth");
            setAuth(false);
          }
        }
      } catch (error) {
        console.error("Auth check error details:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          code: error.code,
        });

        if (isMounted) {
          const localAuth = Cookies.get("isAuthenticated") === "true";
          if (!localAuth) {
            console.log("No local auth found, setting to false");
            setAuth(false);
          } else {
            console.log("Keeping local auth state despite network error");
            setIsLoading(false);
          }
        }
      } finally {
        if (isMounted) {
          console.log("Auth check complete, setting isLoading to false");
          setIsLoading(false);
          authCheckInProgressRef.current = false;
        }
      }
    };

    const safetyTimeout = setTimeout(() => {
      if (isMounted && isLoading) {
        console.warn("Auth check safety timeout triggered");
        setIsLoading(false);

        const localAuth = Cookies.get("isAuthenticated") === "true";
        if (!localAuth) {
          setAuth(false);
        }
      }
    }, 8000);

    checkAuthStatus();

    return () => {
      console.log("AuthProvider cleanup");
      isMounted = false;
      clearTimeout(safetyTimeout);
    };
  }, [setAuth, isLoading]);

  // Memoized context value - UPDATED with new unread count values
  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      setIsAuthenticated: setAuth,
      isLoading,
      setIsLoading,
      username,
      userProfile,
      business,
      setTripIDState,
      TripIDState,
      reviseBillState,
      setReviseBillState,
      moduleChange,
      setModuleChange,
      activeModule,
      setActiveModule: setActiveModuleOptimized,
      searchValue,
      setSearchValue,
      selectedChat,
      setSelectedChat,
      receivedMsg,
      setReceivedMsg,
      filterReports,
      setFilterReports: setFilterReportsOptimized,
      filterBills,
      setFilterBills: setFilterBillsOptimized,
      user: { username, profile: userProfile },
      // NEW: Add unread count values
      totalUnreadCount,
      refreshUnreadCount,
      decrementUnreadCount,
      resetUnreadCount,
      // ADD THIS: Function to sync unread count from chat updates
      syncUnreadCountFromChats: (chats) => {
        const total = chats.reduce(
          (sum, chat) => sum + (chat.unreadCount || 0),
          0,
        );
        setTotalUnreadCount(total);
      },
    }),
    [
      isAuthenticated,
      setAuth,
      isLoading,
      username,
      userProfile,
      business,
      TripIDState,
      reviseBillState,
      moduleChange,
      activeModule,
      setActiveModuleOptimized,
      searchValue,
      selectedChat,
      receivedMsg,
      filterReports,
      setFilterReportsOptimized,
      filterBills,
      setFilterBillsOptimized,
      totalUnreadCount,
      refreshUnreadCount,
      decrementUnreadCount,
      resetUnreadCount,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
export { AuthContext };
